// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import { ApiParameter, del, get, post, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";

const dropShipUrl = "api/dropshipaddresses/";
const validateAddressUri = "/api/v2/address/validation";
const getDropShipAddressUri = `${dropShipUrl}getdropshipaddresses/`;
const addDropShipAddressUri = `${dropShipUrl}adddropshipaddress/`;
const deleteDropShipAddressUri = `${dropShipUrl}deletedropshipaddress`;

const applyCodeTermsUri = `api/customerordercustomization/applytermscode`;

interface TimeStampedApiParamter extends ApiParameter {
    timestamp?: string;
}

export interface AddressValidationResponse {
    addressSuggestions: AddressSuggestion[];
    requestAddress: AddressSuggestion;
    errorMessage: string;
}
export interface AddressValidationRequest extends TimeStampedApiParamter {
    streetAddress1: string;
    streetAddress2: string;
    city: string;
    state?: string;
    country?: string;
    postalCode: string;
    addressType?: string;
    email?: string;
    phone?: string;
    companyName?: string;
    attention?: string;
    isServiceValidatedAddress?: boolean;
    firstName?: string;
    lastName?: string;
}
export interface AddressSuggestion {
    streetAddress1: string;
    streetAddress2: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
    isRequestedAddress: boolean;
    addressType: string;
    isServiceValidatedAddress: boolean;
}

export async function validateAddress(
    params: AddressValidationRequest,
): Promise<ServiceResult<AddressValidationResponse>> {
    try {
        const result = await get<AddressValidationResponse>(validateAddressUri, params);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function getDropShipAddresses(): Promise<ServiceResult<any>> {
    const timestamp = `time${new Date().getTime()}`;
    const params = {
        timestamp,
    } as TimeStampedApiParamter;
    try {
        const result = await get(getDropShipAddressUri, params);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export interface AddDropShipAddressParameters extends TimeStampedApiParamter {
    address?: ShipToModel;
}

export async function addDropShipAddress(params: AddDropShipAddressParameters) {
    try {
        const result = await post(`${addDropShipAddressUri}`, params.address);
        return {
            result,
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function deleteDropshipAddress(params: { id: string } & ApiParameter): Promise<ServiceResult<void>> {
    try {
        const result = await del(`${deleteDropShipAddressUri}/${params.id}`);
        return {
            result,
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        if (error?.status === 404) {
            return {
                successful: false,
                errorMessage: error.errorMessage,
            };
        }
        return error;
    }
}

export async function getCustomerOrderSource() {
    try {
        const result = await get(`api/lookups/getcustomerordersources`);
        return {
            result,
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

// Don't know what is this for, but from the old implementation, they are calling this after payment method change. I would assume this is OOTB at this step?
// For now I will follow what is been done before by calling this inside setCheckoutPaymentMethod handler to match old behavior
export async function updateCartTermsCode(params: { termsCode: string } & ApiParameter) {
    try {
        const result = await post(`${applyCodeTermsUri}?termsCode=${params.termsCode}`);
        return {
            result,
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        if (error?.status === 404) {
            return {
                successful: false,
                errorMessage: error.errorMessage,
            };
        }
        return error;
    }
}
